import { SendBox } from "@azure/communication-react";
import { useState } from "react";
import {
	CCAttachmentMetadataInProgress,
	MediaTypeLimits,
	MimeToWhatsAppTypeMap,
	WhatsAppMessageType,
} from "./types";
import { useIntl } from "react-intl";
import { MessagingComposerShell } from "./MessagingComposerShell";

interface TextEditorProps {
	sendMessages: (
		content: string,
		attachments?: CCAttachmentMetadataInProgress[]
	) => void;
	allowAttachments: boolean;
	isDisabled?: boolean;
}

export const TextEditor: React.FC<TextEditorProps> = ({
	sendMessages,
	allowAttachments = true,
	isDisabled,
}) => {
	const [attachments, setAttachments] = useState<
		CCAttachmentMetadataInProgress[]
	>([]);

	const intl = useIntl();
	const [systemMessage, setSystemMessage] = useState("");

	const displayErrorMessage = (message: string) => {
		setSystemMessage(message);
		setTimeout(() => {
			setSystemMessage("");
		}, 5000);
	};

	const validateFiles = (files: File[]) => {
		if (!files || files.length === 0) {
			return { isValid: false, message: "" };
		}

		const errors: string[] = [];

		if (files.length + attachments.length > 5) {
			errors.push(
				intl.formatMessage({
					id: "Interaction.WhatsAppContent.MaxFilesUpload",
				})
			);
		}

		const newFiles = Array.from(files);
		const fileNames = attachments.map((a) => a.name);

		const duplicateFiles = newFiles.filter((file) =>
			fileNames.some((name) => name === file.name)
		);

		if (duplicateFiles.length > 0) {
			errors.push(
				intl.formatMessage({
					id: "Interaction.WhatsAppContent.AlreadyAttached",
				})
			);
		}

		newFiles.forEach((file) => {
			let maxSize = MediaTypeLimits[file.type];

			//.amr extension is not correctly detected
			if (file.name.endsWith(".amr")) {
				maxSize = MediaTypeLimits["audio/amr"];
			}

			if (!maxSize) {
				errors.push(
					intl.formatMessage(
						{
							id: "Interaction.WhatsAppContent.FileExtensionNotSupported",
						},
						{
							filename: file.name,
						}
					)
				);
			} else if (file.size > maxSize) {
				errors.push(
					intl.formatMessage(
						{
							id: "Interaction.WhatsAppContent.FileSizeTooLarge",
						},
						{
							filename: file.name,
							size: (maxSize / (1024 * 1024)).toFixed(2),
						}
					)
				);
			}
		});

		if (errors.length > 0) {
			return { isValid: false, message: errors.join(" ") };
		}

		return { isValid: true, message: "" };
	};

	const processFiles = (files: File[]) => {
		setSystemMessage("");

		const validationResult = validateFiles(files);

		if (!validationResult.isValid) {
			displayErrorMessage(validationResult.message);

			return;
		}

		const newAttachments = files.map((file) => {
			let type =
				MimeToWhatsAppTypeMap[file.type] ?? WhatsAppMessageType.Unknown;

			//.amr extension is not correctly detected
			if (file.name.endsWith(".amr")) {
				type = WhatsAppMessageType.Audio;
			}

			return {
				id: crypto.randomUUID(),
				name: file.name,
				url: URL.createObjectURL(file),
				progress: 1,
				type: type,
				file: file,
			};
		});

		setAttachments((prevAttachments) => [
			...prevAttachments,
			...newAttachments,
		]);
	};

	const handleTyping = async () => {
		if (systemMessage !== "") {
			setSystemMessage("");
		}
	};

	return (
		<MessagingComposerShell
			processFiles={processFiles}
			allowAttachments={allowAttachments}
			isDisabled={isDisabled}
		>
			<SendBox
				supportNewline
				attachments={attachments}
				onTyping={handleTyping}
				onSendMessage={async (content: string) => {
					sendMessages(content, attachments);
					setAttachments([]);
				}}
				onCancelAttachmentUpload={(attachmentId: string) => {
					setAttachments(attachments.filter((a) => a.id !== attachmentId));
				}}
				systemMessage={systemMessage}
				strings={{
					placeholderText: intl.formatMessage({
						id: "Interaction.TextEditor.TypeAMessage",
					}),
				}}
				styles={{
					systemMessage: {
						whiteSpace: "pre-line",
					},
				}}
				disabled={isDisabled}
			/>
		</MessagingComposerShell>
	);
};
